import '../css/EventFlyer.css';
import { useUser } from '../hooks/useUser';
import { useState, useEffect, useMemo } from 'react';
import { formatDateTime } from '../services/formatTime';
import { useNavigate, useParams } from 'react-router-dom';
import { EventDetails } from '../interfaces/userInterfaces';
import { parseLocalDate } from '../components/Calendar/CalendarHelpers/calendarUtils';
import {
  addEventToAttending,
  cancelAttendEvent,
  deleteEventRow,
  getEventById,
} from '../services/fetch-utils';

const EMPTY_EVENT: EventDetails = {
  address: { address: '', city: '', state: '', zipCode: '' },
  created_at: '',
  date: '',
  description: '',
  event_name: '',
  id: 0,
  promotional_image: '',
  time: '',
  user_id: '',
  attending_event: [{ count: 0 }],
};

export const EventFlyer: React.FC = () => {
  const { user, userAttendingEventsList, updateUserEventsListAfterAction } = useUser();
  const navigate = useNavigate();
  const { id } = useParams();

  const [eventDetails, setEventDetails] = useState<EventDetails>(EMPTY_EVENT);

  // Derive attendance from context — always accurate, even on direct navigation
  const isAttending = useMemo(
    () => userAttendingEventsList?.includes(eventDetails.id) ?? false,
    [userAttendingEventsList, eventDetails.id]
  );

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      const res = await getEventById(id);
      if (res.status === 200 && res.data?.user_id && res.data?.event_name) {
        setEventDetails(res.data);
      }
    };
    load();
  }, [id]);

  const handleDelete = async () => {
    const res = await deleteEventRow(eventDetails.id);
    if (res.status === 204) {
      await updateUserEventsListAfterAction(user?.user_id!);
      navigate('/profile');
    }
  };

  const handleAttend = async () => {
    const res = await addEventToAttending(eventDetails.id, user!.user_id);
    if (res.status === 201) {
      await updateUserEventsListAfterAction(user?.user_id!);
    }
  };

  const handleCancelAttend = async () => {
    const res = await cancelAttendEvent(eventDetails.id, user!.user_id);
    if (res.status === 204) {
      await updateUserEventsListAfterAction(user?.user_id!);
    }
  };

  // Use parseLocalDate to avoid the Safari UTC-offset bug
  const eventTimeInfo = eventDetails.date
    ? formatDateTime(parseLocalDate(eventDetails.date), eventDetails.time)
    : '';

  const attendeeCount = eventDetails.attending_event?.[0]?.count ?? 0;
  const isOwner = eventDetails.user_id === user?.user_id;

  return (
    <div id="event-detail-container">
      <div className="hero-image">
        <img
          style={{ borderRadius: '10px', height: '100%' }}
          src={
            eventDetails.promotional_image ||
            'https://nqbvdgzoxvmdlnjovyqu.supabase.in/storage/v1/object/public/avatars/default-avatar/userexpression.png'
          }
          alt={eventDetails.event_name}
        />
        <div>
          <h1>{eventDetails.event_name}</h1>
          <p>
            <time dateTime={`${eventDetails.date}T${eventDetails.time}`}>
              {eventTimeInfo}
            </time>
          </p>

          {eventDetails.description && <p>{eventDetails.description}</p>}

          <address>
            {eventDetails.address?.address}<br />
            {eventDetails.address?.city}, {eventDetails.address?.state} {eventDetails.address?.zipCode}
          </address>

          <p>
            {attendeeCount} {attendeeCount === 1 ? 'person has' : 'people have'} added this event to their calendar
          </p>

          {isOwner && (
            <button className="btn-ghost" onClick={handleDelete}>
              Delete Event
            </button>
          )}

          {!isOwner && (
            isAttending ? (
              <button className="btn-ghost" onClick={handleCancelAttend}>
                Remove from Calendar
              </button>
            ) : (
              <button className="btn-primary" onClick={handleAttend}>
                Add to Calendar
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};
